import { queryClient } from 'config/queryClient';
import { toast } from 'react-toastify';
import { alertOptions } from 'utils/Config';
import Storage from 'utils/Storage';

import { SSE_EVENTS } from '../constants/sseEvents';
import { useSSEEventAll } from './useSSE';

const SSE_MESSAGES: Record<string, string> = {
  [SSE_EVENTS.APPROVED]: 'Job approved',
  [SSE_EVENTS.CANDIDATES_MATCHED]: 'New candidates matched',
  [SSE_EVENTS.JOB_ASSIGNED]: 'A new job is assigned to you',
  [SSE_EVENTS.JOB_CLOSED]: 'Job has been closed',
  [SSE_EVENTS.JOB_CREATED]: 'A new job has been created',
  [SSE_EVENTS.JOB_PUBLISHED]: 'Job published',
  [SSE_EVENTS.JOB_STATUS_CHANGED]: 'Job status has been updated',
  [SSE_EVENTS.JOB_UPDATED]: 'A job has been updated',
  [SSE_EVENTS.SHORTLISTED]: 'Candidate(s) shortlisted',
  [SSE_EVENTS.SUBMITTED]: 'Job submitted',
  [SSE_EVENTS.SUBMITTED_FOR_REVIEW]: 'Submitted for review',
};

// Queue to display SSE toast notifications one-by-one
const toastQueue: Array<{ message: string; options: any }> = [];
let isProcessingQueue = false;

const processToastQueue = () => {
  if (isProcessingQueue || toastQueue.length === 0) return;
  isProcessingQueue = true;

  const next = toastQueue.shift();
  if (next) {
    toast.info(next.message, {
      ...next.options,
      onClose: () => {
        setTimeout(() => {
          isProcessingQueue = false;
          processToastQueue();
        }, 500); // 500ms delay between toasts
      },
    });
  }
};

const showQueuedToast = (message: string, options: any) => {
  toastQueue.push({ message, options });
  processToastQueue();
};

/**
 * Global hook to handle common SSE events like job assignments,
 * status changes, and refreshing the job queue.
 */
export const useGlobalSSEHandler = () => {
  useSSEEventAll((event) => {
    const { type, data } = event;

    if (type === 'job_scored') {
      const js = data?.job_scored;
      const scoredJobId =
        (typeof js === 'object' && js !== null ? js.jobId || js.id : js) ||
        data?.jobId ||
        data?.id ||
        (event as any)?.jobId ||
        (event as any)?.id;

      const isCurrentJob =
        scoredJobId && window.location.pathname.includes(String(scoredJobId));

      if (isCurrentJob) {
        void queryClient.invalidateQueries({ queryKey: ['job-board-results'] });
        void queryClient.invalidateQueries({ queryKey: ['job-posting'] });
      }
      return;
    }

    const message =
      SSE_MESSAGES[type as keyof typeof SSE_MESSAGES] || data?.message;

    if (message) {
      const user = Storage.isTokenValidDetails();

      if (type === SSE_EVENTS.JOB_ASSIGNED) {
        // Special check: Only show toast if assigned to current user
        const isAssignedToMe = Array.isArray(data?.assignedTo)
          ? data.assignedTo.includes(user?.id)
          : user && user.id === data?.assignedTo;

        if (isAssignedToMe) {
          showQueuedToast(message, alertOptions);
          if (window.location.pathname.includes('job-queue')) {
            queryClient.invalidateQueries();
          }
        }
      } else {
        const eventJobId = data?.jobId || data?.id;
        const isCurrentJob =
          eventJobId && window.location.pathname.includes(String(eventJobId));
        const isOnViewJob = window.location.pathname.includes('view-job');

        if (isOnViewJob && isCurrentJob) {
          // User is on the respective job page. Just call the respective API.
          if (type === SSE_EVENTS.CANDIDATES_MATCHED) {
            queryClient.invalidateQueries({ queryKey: ['matched-candidates'] });
          } else if (type === SSE_EVENTS.SHORTLISTED) {
            queryClient.invalidateQueries({
              queryKey: ['shortlisted-candidates'],
            });
            queryClient.invalidateQueries({
              queryKey: ['all-shortlisted-candidates'],
            });
          } else if (
            type === SSE_EVENTS.SUBMITTED ||
            type === SSE_EVENTS.SUBMITTED_FOR_REVIEW
          ) {
            queryClient.invalidateQueries({
              queryKey: ['support-approved-candidates'],
            });
            queryClient.invalidateQueries({
              queryKey: ['all-shortlisted-candidates'],
            });
          } else {
            // Default to refreshing job posting data for status updates
            queryClient.invalidateQueries({ queryKey: ['job-posting'] });
          }
        } else {
          // User is NOT on the respective page. Show toast.
          showQueuedToast(message, alertOptions);

          // Auto-refresh the job queue for job-related updates
          const jobRelatedEvents: string[] = [
            SSE_EVENTS.JOB_CREATED,
            SSE_EVENTS.JOB_UPDATED,
            SSE_EVENTS.JOB_STATUS_CHANGED,
            SSE_EVENTS.JOB_CLOSED,
            SSE_EVENTS.JOB_PUBLISHED,
            SSE_EVENTS.APPROVED,
          ];

          if (
            jobRelatedEvents.includes(type) &&
            window.location.pathname.includes('job-queue')
          ) {
            queryClient.invalidateQueries();
          }
        }
      }
    }
  });
};
